import { create } from 'zustand';
import type { ParsedObjectUpdate } from '../components/chat/objectUpdateParser';
import { stageObjectUpdate } from '../components/chat/objectDraftRegistry';
import {
  CUSTOM_ROLE_OBJECT_KIND,
  CUSTOM_ROLE_CREATE_DRAFT_ID,
} from '../utils/chatEditableObjectUtils';
import { markObjectUpdatesConsumed } from './consumedObjectUpdatesStore';
import { useRoleEditStore } from './roleEditStore';
import { navigateToApplicationSettingsSubTab } from '../utils/navigateToApplicationSettingsSubTab';

function mergeTrackedRoleIds({
  existingIds,
  updates,
}: {
  existingIds: string[];
  updates: ParsedObjectUpdate[];
}): string[] {
  const ids = new Set(existingIds);
  for (const update of updates) {
    if (update.objectKind === CUSTOM_ROLE_OBJECT_KIND) {
      ids.add(update.objectId);
    }
  }
  return [...ids];
}

type CustomRoleUpdateQueueStore = {
  queue: ParsedObjectUpdate[];
  activeUpdate: ParsedObjectUpdate | null;
  sourceChatId: string | null;
  trackedRoleIds: string[];
  hasActiveCustomRoleQueueWork: () => boolean;
  hasPendingCustomRoleWork: () => boolean;
  enqueueCustomRoleUpdates: ({
    updates,
    chatId,
  }: {
    updates: ParsedObjectUpdate[];
    chatId: string;
  }) => void;
  tryActivateNextCustomRoleUpdate: () => void;
  completeActiveCustomRoleUpdate: ({
    roleId,
    updateId,
    preserveRoleEditState,
  }: {
    roleId: string;
    updateId?: string;
    preserveRoleEditState?: boolean;
  }) => void;
  clearCustomRoleUpdateQueue: () => void;
  releaseCustomRoleUpdateStateForChat: (chatId: string) => void;
};

export const useCustomRoleUpdateQueueStore = create<CustomRoleUpdateQueueStore>(
  (set, get) => ({
    queue: [],
    activeUpdate: null,
    sourceChatId: null,
    trackedRoleIds: [],

    hasActiveCustomRoleQueueWork: () => {
      const state = get();
      return state.activeUpdate !== null || state.queue.length > 0;
    },

    hasPendingCustomRoleWork: () => get().hasActiveCustomRoleQueueWork(),

    enqueueCustomRoleUpdates: ({ updates, chatId }) => {
      const customRoleUpdates = updates.filter(
        (update) => update.objectKind === CUSTOM_ROLE_OBJECT_KIND,
      );
      if (customRoleUpdates.length === 0) {
        return;
      }

      const state = get();
      const knownIds = new Set([
        ...state.queue.map((entry) => entry.id),
        ...(state.activeUpdate ? [state.activeUpdate.id] : []),
      ]);
      const nextQueue = [...state.queue];
      for (const update of customRoleUpdates) {
        if (knownIds.has(update.id)) {
          continue;
        }
        knownIds.add(update.id);
        nextQueue.push(update);
      }

      set({
        queue: nextQueue,
        sourceChatId: state.sourceChatId ?? chatId,
        trackedRoleIds: mergeTrackedRoleIds({
          existingIds: state.trackedRoleIds,
          updates: customRoleUpdates,
        }),
      });

      if (get().activeUpdate === null) {
        get().tryActivateNextCustomRoleUpdate();
      }
    },

    tryActivateNextCustomRoleUpdate: () => {
      const state = get();
      if (state.activeUpdate !== null) {
        return;
      }

      const [nextUpdate, ...remainingQueue] = state.queue;
      if (!nextUpdate) {
        return;
      }

      if (nextUpdate.objectId === CUSTOM_ROLE_CREATE_DRAFT_ID) {
        useRoleEditStore
          .getState()
          .clearRoleEditState(CUSTOM_ROLE_CREATE_DRAFT_ID);
      }

      const staged = stageObjectUpdate({
        objectKind: nextUpdate.objectKind,
        objectId: nextUpdate.objectId,
        patch: nextUpdate.patch,
        reason: nextUpdate.reason,
        sourceUpdateId: nextUpdate.id,
      });
      const pendingFieldCount =
        useRoleEditStore.getState().getPendingAiEdits(nextUpdate.objectId)
          ?.fields.length ?? 0;
      if (!staged || pendingFieldCount === 0) {
        markObjectUpdatesConsumed([nextUpdate.id]);
        set({ queue: remainingQueue });
        get().tryActivateNextCustomRoleUpdate();
        return;
      }

      navigateToApplicationSettingsSubTab('roles');
      set({
        queue: remainingQueue,
        activeUpdate: nextUpdate,
      });
    },

    completeActiveCustomRoleUpdate: ({
      roleId,
      updateId,
      preserveRoleEditState,
    }) => {
      const state = get();
      const { activeUpdate, queue } = state;
      const consumedUpdateIds = new Set<string>(
        useRoleEditStore.getState().listPendingSourceUpdateIds(roleId),
      );

      const completingActiveUpdate =
        activeUpdate &&
        (updateId
          ? activeUpdate.id === updateId
          : activeUpdate.objectId === roleId)
          ? activeUpdate
          : null;

      if (completingActiveUpdate) {
        consumedUpdateIds.add(completingActiveUpdate.id);
        if (!preserveRoleEditState) {
          useRoleEditStore
            .getState()
            .clearRoleEditState(completingActiveUpdate.objectId);
        }
      } else if (roleId !== CUSTOM_ROLE_CREATE_DRAFT_ID) {
        for (const queuedUpdate of queue) {
          if (queuedUpdate.objectId === roleId) {
            consumedUpdateIds.add(queuedUpdate.id);
          }
        }
      }

      const nextQueue =
        completingActiveUpdate || roleId === CUSTOM_ROLE_CREATE_DRAFT_ID
          ? queue
          : queue.filter((queuedUpdate) => queuedUpdate.objectId !== roleId);
      const nextActiveUpdate = completingActiveUpdate ? null : activeUpdate;

      if (consumedUpdateIds.size > 0) {
        markObjectUpdatesConsumed([...consumedUpdateIds]);
      }

      set({
        activeUpdate: nextActiveUpdate,
        queue: nextQueue,
      });

      if (nextQueue.length > 0 && nextActiveUpdate === null) {
        get().tryActivateNextCustomRoleUpdate();
      }
    },

    clearCustomRoleUpdateQueue: () => {
      set({
        queue: [],
        activeUpdate: null,
        sourceChatId: null,
        trackedRoleIds: [],
      });
    },

    releaseCustomRoleUpdateStateForChat: (chatId) => {
      const state = get();
      if (state.sourceChatId !== chatId) {
        return;
      }

      const roleEditStore = useRoleEditStore.getState();
      const sourceUpdateIds: string[] = [];

      for (const update of state.queue) {
        sourceUpdateIds.push(update.id);
      }
      if (state.activeUpdate) {
        sourceUpdateIds.push(state.activeUpdate.id);
      }

      for (const roleId of state.trackedRoleIds) {
        roleEditStore.clearRoleEditState(roleId);
      }
      roleEditStore.clearPendingEditsForSourceUpdateIds(sourceUpdateIds);

      set({
        queue: [],
        activeUpdate: null,
        sourceChatId: null,
        trackedRoleIds: [],
      });
    },
  }),
);
